/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Headline, LinkButton, Title } from '@cambridgeassessment/cambridge-ui';
import {
  DropdownContainer,
  StyledConfigurationText,
  StyledContainer,
  StyledComponentParagraph,
} from '../Content.styles';
import { RootState } from '../../../store';
import Loader from '../../../components/Loader/Loader';
import thunks, { setQualificationAndAssessment } from '../../../store/qualifications/thunks';
import componentThunks from '../../../store/components/thunks';
import seriesThunks from '../../../store/series/thunks';
import { deleteAllComponents } from '../../../store/components/actions';
import { setAssessment, setAssessmentVersion, setAssessmentVersions } from '../../../store/qualifications/actions';
import { Qualification } from '../../../service/qualifications/types';
import Assessments from './Assessments';
import ComponentsDetail from './Details';
import AssessmentVersions from './AssessmentVersions';
import { Assessment } from '../../../types/Syllabuses';
import { StyledModerationLabelContainer, useStyles, StyledReportLink } from './Details/styles';
import { ReportType, Roles } from '@cambridgeassessment/lib-ee-commons';
import DotsLoader from '../../../components/DotsLoader/DotsLoaderFC';
import { BoldParagraph, StyledParagraph } from './AssessmentVersions/styles';
import { Authorized } from 'lib-browser';

const Components: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { businessUnit, selectedRole } = useSelector((state: RootState) => state.userData);
  const { qualificationData, isLoading, hasError, qualification } = useSelector(
    (state: RootState) => state.qualifications,
  );
  const activeSeriesIds = useSelector((state: RootState) => state.series.activeSeriesIds);
  const futureSeriesIds = useSelector((state: RootState) => state.series.futureSeriesIds);
  const { components } = useSelector((state: RootState) => state.components);
  const { assessment, assessmentVersions } = useSelector((state: RootState) => state.qualifications);
  const { isReportBeingGenerated } = useSelector((state: RootState) => state.components);
  const [assessmentCode, setAssessmentCode] = useState('');
  const [stateAssessment, setStateAssessment] = useState<Assessment>();

  const getAssessmentVersions = useCallback(
    (selectedAssessment: Assessment) => {
      if (businessUnit?.id && qualification?.id) {
        dispatch(thunks.getAssessmentVersions(businessUnit.id, qualification.id, selectedAssessment.code));
      }
    },
    [businessUnit?.id, qualification?.id, dispatch],
  );

  useEffect(() => {
    if (businessUnit) {
      dispatch(thunks.getQualificationData(businessUnit.id));
      dispatch(seriesThunks.getSeriesData(businessUnit.id));
      dispatch(seriesThunks.getActiveSeries(businessUnit.id));
    }
    return () => {
      dispatch(deleteAllComponents());
    };
  }, [businessUnit, dispatch]);

  useEffect(() => {
    if (!assessment || assessment.code === assessmentCode) {
      return;
    }

    setAssessmentCode(assessment.code);
    getAssessmentVersions(assessment);
  }, [assessment, assessmentCode, getAssessmentVersions]);

  useEffect(() => {
    if (!stateAssessment) {
      return;
    }
    if (!assessmentVersions) {
      getAssessmentVersions(stateAssessment);
    }

    const versionAvailability = assessmentVersions?.find(
      (version) => version.code === stateAssessment.code && version.version === stateAssessment.version,
    );
    dispatch(setAssessment({ ...stateAssessment, availableIn: versionAvailability?.availableIn }));
  }, [stateAssessment, assessmentVersions]);

  useEffect(() => {
    if (assessment?.code && assessment?.version && businessUnit?.id && qualification?.id && activeSeriesIds) {
      if (activeSeriesIds.length) {
        dispatch(
          componentThunks.getComponentsWithEntriesCheck(
            businessUnit.id,
            activeSeriesIds.concat(futureSeriesIds),
            qualification.id,
            assessment.code,
            assessment.version,
          ),
        );
      } else {
        dispatch(componentThunks.getComponents(businessUnit.id, qualification.id, assessment.code, assessment.version));
      }
    }
  }, [assessment?.code, assessment?.version, businessUnit?.id, qualification?.id, activeSeriesIds, dispatch]);

  const renderQualification = (selectedQualification: Qualification): string => selectedQualification.name;

  if (isLoading) return <Loader />;

  if (hasError) return <div>Failed to get Qualifications data</div>;

  const generateReportLink = (): JSX.Element => {
    const handleReportClick = (): void => {
      dispatch(componentThunks.getComponentRulesReport(ReportType.COMPONENT_BUSINESS_RULES));
    };
    return (
      <StyledReportLink>
        {isReportBeingGenerated ? (
          <StyledModerationLabelContainer>
            <DotsLoader dataQaId='dots-loader' rightSide={false} />
          </StyledModerationLabelContainer>
        ) : (
          <LinkButton
            ariaLabel='download-all-component-business-rules-extract'
            dataQaId='components-extract'
            onClick={handleReportClick}
            endIcon={false}
            passedClassName={classes.root}
          >
            Download All Component Business Rules extract
          </LinkButton>
        )}
      </StyledReportLink>
    );
  };

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE]}
      readOnlyRoles={[Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER, Roles.SUBMIT_ADMINISTRATOR]}
    >
      <StyledContainer data-qa-id='page-priming-components'>
        <Headline>Component business rules</Headline>
        <StyledComponentParagraph>Define and set requirements for components.</StyledComponentParagraph>
        {generateReportLink()}
        <Title>Select qualification</Title>
        <DropdownContainer>
          <Dropdown<Qualification>
            data={qualificationData as Qualification[]}
            renderOption={renderQualification}
            renderSelectedOption={renderQualification}
            dataQaId='qualifications-select'
            onChange={(value) => {
              setAssessmentCode('');
              setStateAssessment(undefined);
              dispatch(setAssessmentVersions(undefined));
              dispatch(setQualificationAndAssessment(value));
            }}
            value={qualification ?? null}
            placeholder='Please choose a qualification'
            title='Select Qualification'
          />
        </DropdownContainer>
        {qualification && (
          <Assessments assessments={qualification.assessments} assessment={assessment} onChange={setStateAssessment} />
        )}
        {assessment && assessmentVersions && assessmentVersions.length > 1 && (
          <AssessmentVersions
            assessments={assessmentVersions}
            assessment={assessment}
            onChange={(value) => dispatch(setAssessmentVersion(value.version, value.availableIn))}
          />
        )}
        {components?.some((component) => component.toBeConfirmed) && (
          <StyledConfigurationText>
            You must review and save copied component configurations before they can be used. They have been copied from
            the previous syllabus version to this syllabus version, for all components where numbers match.
          </StyledConfigurationText>
        )}
        {assessment && assessment.availableIn && !!assessment.availableIn.length && (
          <>
            <BoldParagraph data-qa-id='availability-title'>
              Availability of the syllabus version per series:
            </BoldParagraph>
            {assessment.availableIn.map((el: string, index: number) => (
              <StyledParagraph key={index}>{el}</StyledParagraph>
            ))}
          </>
        )}
        {qualification && assessment && <ComponentsDetail />}
      </StyledContainer>
    </Authorized>
  );
};

export default Components;
